/**
 * Página unificada de detalle: maneja `/student/story/:id` (historias) y
 * `/student/conversations/:id` (prácticas de conversación) compartiendo la
 * intro y el flujo hasta que el alumno pulsa «Empezar».
 *
 *  Fase 'intro'      → hero + ContentDetailIntro + barra inferior con «Empezar».
 *  Fase 'consuming'  → ContentDetailIntro + recursos (historias) o chat (conversación).
 *  Fase 'activities' → ActivitiesTab a pantalla completa (sin intro ni hero).
 *
 * Tras el envío, navega a `/student/result/:id` con el state correspondiente
 * a cada tipo (story → submitChallenge, conversation → submitConversationActivities).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  storyResourcesEntrance,
  entranceAwareHeroTransition,
} from '@/constants/storyResourcesEntrance';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { useXP } from '@/context/XPContext';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { useStoryLayoutHero } from '@/context/StoryLayoutHeroContext';
import { usePreviewMode } from '@/context/PreviewModeContext';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { ContentDetailIntro } from '@/components/organisms/ContentDetailIntro';
import { useTimer } from '@/hooks/useTimer';
import { useStudyTimeTracker } from '@/hooks/useStudyTimeTracker';
import { usePublishedConversationPractice } from '@/hooks/useConversationData';
import {
  getStoryById,
  getStoryProgress,
  submitStory,
  updateStoryTime,
} from '@/services/api/stories.api';
import {
  submitConversationActivities,
  type ConversationChatAccuracy,
} from '@/services/api/conversations.api';
import { usersAPI } from '@/services/api/users.api';
import { ActivitiesTab } from '../StoryPage/components/ActivitiesTab';
import { ROUTES, buildRoute } from '@/constants/routes';
import type { Story, UserAnswer } from '@/types/story.types';
import type { MistakeReviewSessionPayload } from '@/types/mistakeReview.types';
import type { ContentItem } from '@/types/conversation.types';
import type {
  ConversationSituationDocument,
  ConversationSituationInner,
} from '@/types/conversation.types';
import { cn } from '@/utils/cn';
import { localizedField } from '@/utils/localizedField';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import storyStyles from '../StoryPage/StoryPage.module.scss';
import { StoryMorphBar } from './StoryMorphBar';
import { StoryStartedFlow } from './StoryStartedFlow';
import { ConversationStartedFlow } from './ConversationStartedFlow';

type DetailType = 'story' | 'conversation';

type Phase = 'intro' | 'consuming' | 'activities';

/**
 * Imagen hero compartida entre historias y conversaciones. Usa la misma transición
 * de entrada que antes y se monta vía portal a través de
 * `StoryLayoutHeroContext` desde un `setHero(...)` en el padre.
 */
function DetailHeroEntrance({ heroKey, imageUrl }: { heroKey: string; imageUrl: string }) {
  const pageReady = usePageTransitionEntrance();
  const reduced = Boolean(useReducedMotion());
  const hidden = { opacity: 0, y: storyResourcesEntrance.offsetY, scale: 0.95 };
  const shown = { opacity: 1, y: 0, scale: 1 };
  return (
    <motion.div
      key={heroKey}
      className={storyStyles.story__hero}
      initial={hidden}
      animate={pageReady ? shown : hidden}
      transition={entranceAwareHeroTransition(pageReady, reduced)}
    >
      <img
        src={imageUrl}
        alt=""
        className={storyStyles.story__heroImage}
        decoding="async"
      />
    </motion.div>
  );
}

function detectType(pathname: string): DetailType {
  return pathname.includes('/conversations/') ? 'conversation' : 'story';
}

export function StoryDetailPage() {
  const { t, i18n } = useTranslation();
  const { labelFor } = useCategoryLabels();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = usePreviewMode();
  const { refreshXp } = useXP();
  const { setHero } = useStoryLayoutHero();
  const { setContent: setBottomBarContent, setHideFade, setHideCloseButton } =
    useStoryBottomBar() ?? {};

  const type: DetailType = useMemo(() => detectType(location.pathname), [location.pathname]);

  /**
   * Resetea el estado del `StoryBottomBarContext` que pudo quedarse
   * contaminado del flujo anterior. Concretamente: el submit previo de un
   * historia/conversación deja `hideCloseButton=true` (para esconder la X durante
   * el envío) y nunca lo baja en éxito porque navega a `/result`. Cuando el
   * alumno vuelve al grid con «Seguir aprendiendo» y entra en otro detalle,
   * el header se queda compacto (sin X) y el `margin-top:-80px` del
   * `__heroMount` (calculado para un header completo) acaba recortando el
   * hero por arriba. Se ejecuta antes del primer paint con `useLayoutEffect`.
   */
  useLayoutEffect(() => {
    setHideCloseButton?.(false);
    setHideFade?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navState = location.state as
    | {
        // Story state
        fromStoriesTab?: 'weekly' | 'all';
        fromDashboard?: boolean;
        fromReview?: boolean;
        scrollY?: number;
        viewMode?: 'card' | 'list';
        allAssignmentsNextOffset?: number;
        allAssignmentsHasMore?: boolean;
        reviewDueLoadedCount?: number;
        reviewDueHasMore?: boolean;
        // Paginación de la lista de conversaciones (restaurar al volver del result)
        forYouLoaded?: number;
        moreLoaded?: number;
        // Repaso de errores (compartido)
        mistakeReviewSession?: MistakeReviewSessionPayload;
        reviewWrongActivityIds?: string[];
        reviewPreservedAnswers?: Record<string, UserAnswer>;
      }
    | null;

  const locationStateRef = useRef<unknown>(location.state);
  locationStateRef.current = location.state;

  const mistakeReviewSession: MistakeReviewSessionPayload | null = useMemo(() => {
    const s = navState?.mistakeReviewSession;
    if (s?.queueIds?.length) return s;
    const legacy = navState?.reviewWrongActivityIds;
    if (legacy?.length) {
      return {
        queueIds: legacy,
        preservedAnswers: navState?.reviewPreservedAnswers ?? {},
      };
    }
    return null;
  }, [navState]);

  const navigateToMistakeReview = useCallback(
    (session: MistakeReviewSessionPayload) => {
      navigate('.', {
        replace: true,
        state: {
          ...(locationStateRef.current as object),
          mistakeReviewSession: session,
          reviewWrongActivityIds: undefined,
          reviewPreservedAnswers: undefined,
        },
      });
    },
    [navigate]
  );

  // ── Carga de datos por tipo ────────────────────────────────────────────
  const [story, setStory] = useState<Story | null>(null);
  const [storyLoading, setStoryLoading] = useState(type === 'story');
  const [storyError, setStoryError] = useState<string | null>(null);

  useEffect(() => {
    if (type !== 'story' || !id) return;
    let cancelled = false;
    setStoryLoading(true);
    getStoryById(id)
      .then((c) => {
        if (cancelled) return;
        setStory(c);
        setStoryError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setStoryError(err instanceof Error ? err.message : t('story.loadError'));
      })
      .finally(() => {
        if (!cancelled) setStoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, type, t]);

  // ¿Ya escuchó el audio completo en una visita anterior? (dato per-user del
  // backend; el GET /stories/:id cacheado en edge no puede llevarlo). Se
  // resuelve durante la fase intro — de sobra antes de pulsar «Empezar». Si
  // falla o aún no ha llegado, default false = flujo normal (escucha guiada).
  const [audioListened, setAudioListened] = useState(false);
  useEffect(() => {
    if (type !== 'story' || !id) return;
    let cancelled = false;
    setAudioListened(false);
    getStoryProgress(id)
      .then((p) => {
        if (!cancelled) setAudioListened(p.audioListenedAt != null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, type]);

  const conversationFetch = usePublishedConversationPractice(type === 'conversation' ? id : undefined);
  const conversationResource = type === 'conversation' ? conversationFetch.resource : null;
  const conversationLoading = type === 'conversation' ? conversationFetch.isLoading : false;
  const conversationError = type === 'conversation' ? conversationFetch.error : null;

  const conversationDocument: ConversationSituationDocument | null = useMemo(() => {
    if (type !== 'conversation' || !conversationResource) return null;
    const item = conversationResource.content.find(
      (c): c is ContentItem =>
        c.type === 'conversation' && !!c.conversation?.situation?.turns?.length
    );
    return item?.conversation ?? null;
  }, [type, conversationResource]);

  const conversationSituation: ConversationSituationInner | null = conversationDocument?.situation ?? null;

  // ── Estado de fase ─────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('intro');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exitToResult, setExitToResult] = useState(false);
  /**
   * Cuando el alumno pulsa «Empezar actividades» activamos `isLeaving`:
   * cada sección (hero, info de la historia, karaoke) anima su propia opacidad
   * a 0 con su propio timing — primero hero+info (350 ms), luego el
   * karaoke (550 ms). Pasados ≈ 600 ms cambiamos `phase` a `activities`
   * (en ese momento todo el contenido de consuming está invisible) y
   * reseteamos `isLeaving`.
   *
   * Mucho más simple y predecible que apoyarse en variants/AnimatePresence:
   * cada `motion.div` lee directamente `isLeaving` y anima a su ritmo.
   */
  const [isLeaving, setIsLeaving] = useState(false);

  // Si llegamos con sesión de repaso desde resultado, saltar directo a actividades.
  const reviewMistakesQueueLen = mistakeReviewSession?.queueIds.length ?? 0;
  useLayoutEffect(() => {
    if (reviewMistakesQueueLen > 0) setPhase('activities');
  }, [reviewMistakesQueueLen]);

  // Reset al cambiar de id.
  useEffect(() => {
    setPhase(reviewMistakesQueueLen > 0 ? 'activities' : 'intro');
    setExitToResult(false);
    setIsSubmitting(false);
    setIsLeaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Hero (visible en intro + consuming, oculto en activities) ─────────
  const heroImageUrl: string | null = useMemo(() => {
    if (type === 'story') return story?.backgroundImage ?? null;
    return conversationResource?.backgroundImage ?? null;
  }, [type, story?.backgroundImage, conversationResource?.backgroundImage]);

  /**
   * Hero: `motion.div` con `animate.opacity` directamente ligado a
   * `isLeaving` y a `phase`. Cuando el alumno pulsa «Empezar actividades»
   * (`isLeaving = true`), el hero hace fade-out en 350 ms — antes que el
   * karaoke (que se va en 550 ms con su propio motion.div). Cuando phase
   * llega a `activities`, lo desmontamos para que el body recupere todo
   * el alto.
   */
  useEffect(() => {
    if (!heroImageUrl?.trim()) {
      setHero(null);
      return;
    }
    if (phase === 'activities') {
      setHero(null);
      return;
    }
    const heroKey = `${type}|${id}|${heroImageUrl}`;
    setHero(
      <motion.div
        style={{ width: '100%', height: '100%' }}
        initial={false}
        animate={{ opacity: isLeaving ? 0 : 1 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      >
        <DetailHeroEntrance heroKey={heroKey} imageUrl={heroImageUrl.trim()} />
      </motion.div>
    );
    return () => setHero(null);
  }, [heroImageUrl, id, type, phase, isLeaving, setHero]);

  /**
   * Audio en la historia → habilita el morph del botón principal en la barra
   * inferior cuando el alumno pulsa «Empezar» (label fade out + width
   * shrink + icono play fade in). Sin audio el botón solo cambia el label
   * de «Empezar» a «Empezar actividades».
   */
  const storyHasAudio = useMemo(
    () => type === 'story' && (story?.resources?.some((r) => r.type === 'audio') ?? false),
    [type, story?.resources]
  );

  /**
   * Avance hacia la fase de actividades — para conversaciones sin actividades
   * en su recurso, lo tratamos como envío vacío. Para historias disparamos
   * primero `isLeaving` (cada sección anima su fade-out con su propio
   * tiempo) y, cuando el karaoke ya se ha ido (≈ 600 ms), cambiamos a la
   * fase de actividades.
   */
  const requestActivities = useCallback(() => {
    if (type === 'conversation' && !conversationResource?.activities?.length) {
      void handleSubmitConversation([]);
      return;
    }
    setIsLeaving(true);
    window.setTimeout(() => {
      setPhase('activities');
      setIsLeaving(false);
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, conversationResource?.activities?.length]);

  /**
   * Barra inferior unificada para historias: la misma instancia de
   * `StoryMorphBar` permanece montada entre intro y consuming, así Framer
   * puede animar la transformación del botón. Para conversaciones, el intro
   * lo gestiona aquí (botón «Empezar» simple) y consuming lo cede al
   * `ConversationStartedFlow`. La fase de actividades la gestiona
   * `ActivitiesTab` directamente.
   */
  useEffect(() => {
    if (phase === 'activities') return;

    if (type === 'story') {
      setBottomBarContent?.(
        <StoryMorphBar
          phase={phase}
          hasAudio={storyHasAudio}
          onEmpezar={() => setPhase('consuming')}
          onActivities={requestActivities}
        />
      );
      return;
    }

    if (phase === 'intro') {
      setBottomBarContent?.(
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className={storyStyles.story__portalButton}
          onClick={() => setPhase('consuming')}
        >
          {t('activities.start', 'Empezar')}
        </Button>
      );
      return;
    }

    // Conversation consuming: ConversationStartedFlow asume el control.
    setBottomBarContent?.(null);
  }, [phase, type, storyHasAudio, requestActivities, setBottomBarContent, t]);

  useEffect(() => {
    return () => setBottomBarContent?.(null);
  }, [setBottomBarContent]);

  // El degradé inferior se oculta al pasar a actividades (en consuming lo gestiona cada flujo).
  useEffect(() => {
    if (phase !== 'activities') return;
    setHideFade?.(true);
    return () => setHideFade?.(false);
  }, [phase, setHideFade]);

  // Reset scroll al top al entrar a la página y al cambiar de fase a actividades.
  useEffect(() => {
    if (phase !== 'activities') return;
    const scroller = document.querySelector<HTMLElement>('[data-story-scroll-container]');
    if (scroller) scroller.scrollTop = 0;
  }, [phase]);

  useEffect(() => {
    const scroller = document.querySelector<HTMLElement>('[data-story-scroll-container]');
    if (scroller) scroller.scrollTop = 0;
  }, []);

  // ── Timer de la historia: ChallengeProgress (reanudar) + tiempo de estudio en vivo ──
  const challengeLastSentRef = useRef(0);
  const challengeTimeRef = useRef(0);

  const handleStoryTimeUpdate = useCallback(
    (timeSpent: number) => {
      if (isPreview || !id || type !== 'story') return;
      // (a) Progreso reanudable de la historia (tiempo absoluto de la sesión).
      void updateStoryTime(id, timeSpent).catch((err: unknown) => {
        console.error('Failed to save story time:', err);
      });
      // (b) Tiempo de estudio diario en vivo (delta): cuenta aunque no se envíe.
      const delta = Math.round(timeSpent - challengeLastSentRef.current);
      if (delta >= 1) {
        challengeLastSentRef.current = timeSpent;
        void usersAPI.addStoryResourceStudyDelta(delta).catch(() => {
          /* sin bloqueo de lectura */
        });
      }
    },
    [id, isPreview, type]
  );

  const challengeTimer = useTimer({
    onUpdate: handleStoryTimeUpdate,
    autoSaveInterval: 30,
  });
  challengeTimeRef.current = challengeTimer.time;

  // El timer de la historia arranca al montar; al desmontar envía el delta residual
  // para no perder los últimos segundos al enviar o salir.
  useEffect(() => {
    if (type !== 'story') return;
    challengeTimer.start();
    return () => {
      if (isPreview) return;
      const delta = Math.round(challengeTimeRef.current - challengeLastSentRef.current);
      if (delta >= 1) {
        challengeLastSentRef.current = challengeTimeRef.current;
        void usersAPI.addStoryResourceStudyDelta(delta).catch(() => {
          /* sin bloqueo de lectura */
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Conversaciones: la fase de chat la cronometra ConversationStartedFlow; la
  // fase de actividades, este tracker. Ambos envían el tiempo a la tabla diaria
  // de historias/recursos en vivo — el `submit` ya no la alimenta (evita doble conteo).
  const conversationStudySecondsRef = useRef(0);
  const onConversationStudySeconds = useCallback((seconds: number) => {
    conversationStudySecondsRef.current = seconds;
  }, []);
  const { time: conversationActivitiesTime } = useStudyTimeTracker(
    type === 'conversation' && phase === 'activities',
    usersAPI.addStoryResourceStudyDelta
  );

  // ── Submit por tipo ────────────────────────────────────────────────────
  const handleSubmitStory = useCallback(
    async (answers: UserAnswer[]) => {
      if (!id || !story) return;
      if (isPreview) {
        alert(t('story.previewAlert'));
        return;
      }
      setIsSubmitting(true);
      setHideCloseButton?.(true);
      try {
        const result = await submitStory(id, { answers, timeSpent: challengeTimer.time });
        if (result.xpEarned > 0) {
          await refreshXp();
        }
        const activityObjectiveMap: Record<string, string> = {};
        for (const activity of story.activities) {
          if (activity.learningObjectiveId) {
            activityObjectiveMap[activity.id] = activity.learningObjectiveId;
          }
        }
        setExitToResult(true);
        navigate(buildRoute(ROUTES.STUDENT.RESULT, { id: story.id }), {
          state: {
            source: 'story',
            storyId: result.storyId,
            storyTitle: localizedField(story, 'title', i18n.language),
            score: result.score,
            totalPoints: result.totalPoints,
            earnedPoints: result.earnedPoints,
            xpEarned: result.xpEarned,
            timeSpent: result.timeSpent,
            isPassed: result.passed,
            answers: result.activityResults,
            fromStoriesTab: navState?.fromStoriesTab,
            fromDashboard: navState?.fromDashboard,
            fromReview: navState?.fromReview,
            scrollY: navState?.scrollY,
            viewMode: navState?.viewMode,
            forYouLoaded: navState?.forYouLoaded,
            moreLoaded: navState?.moreLoaded,
            reviewDueLoadedCount: navState?.reviewDueLoadedCount,
            reviewDueHasMore: navState?.reviewDueHasMore,
            learningObjectives: story.learningObjectives,
            activityObjectiveMap,
          },
        });
      } catch (err) {
        console.error('Failed to submit story:', err);
        setStoryError(err instanceof Error ? err.message : t('story.submitError'));
        setExitToResult(false);
        setIsSubmitting(false);
        setHideCloseButton?.(false);
      }
    },
    [
      id,
      story,
      isPreview,
      challengeTimer.time,
      refreshXp,
      navigate,
      navState,
      setHideCloseButton,
      t,
    ]
  );

  const handleSubmitConversation = useCallback(
    async (answers: UserAnswer[], accuracy?: ConversationChatAccuracy) => {
      if (!id || !conversationResource) return;
      setIsSubmitting(true);
      setHideCloseButton?.(true);
      try {
        const elapsed = Math.round(
          conversationStudySecondsRef.current + conversationActivitiesTime
        );
        const result = await submitConversationActivities(id, {
          answers,
          timeSpent: elapsed,
          accuracy,
        });
        if (result.xpEarned > 0) {
          await refreshXp();
        }
        setExitToResult(true);
        navigate(buildRoute(ROUTES.STUDENT.RESULT, { id: conversationResource.id }), {
          state: {
            source: 'resource',
            resourceId: conversationResource.id,
            storyTitle:
              i18n.language.startsWith('es') && conversationSituation?.title_translation?.trim()
                ? conversationSituation.title_translation
                : conversationSituation?.title ?? conversationResource.title,
            score: result.score,
            totalPoints: result.totalPoints,
            earnedPoints: result.earnedPoints,
            xpEarned: result.xpEarned,
            xpFull: result.xpFull,
            timeSpent: result.timeSpent,
            isPassed: result.passed,
            answers: result.activityResults,
            fromReview: navState?.fromReview,
            scrollY: navState?.scrollY,
            viewMode: navState?.viewMode,
            reviewDueLoadedCount: navState?.reviewDueLoadedCount,
            reviewDueHasMore: navState?.reviewDueHasMore,
            forYouLoaded: navState?.forYouLoaded,
            moreLoaded: navState?.moreLoaded,
          },
        });
      } catch (err) {
        console.error('Failed to submit conversation activities:', err);
        setExitToResult(false);
        setIsSubmitting(false);
        setHideCloseButton?.(false);
      }
    },
    [
      id,
      conversationResource,
      conversationActivitiesTime,
      refreshXp,
      navigate,
      navState,
      setHideCloseButton,
    ]
  );

  // ── Render ─────────────────────────────────────────────────────────────
  const isLoading = type === 'story' ? storyLoading : conversationLoading;
  const errorMessage =
    type === 'story'
      ? storyError
      : conversationError instanceof Error
        ? conversationError.message
        : conversationError;

  if (isLoading) {
    return (
      <div className={storyStyles.story}>
        <div className={storyStyles.story__loading}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (type === 'story' && (errorMessage || !story)) {
    return (
      <div className={storyStyles.story}>
        <p>{errorMessage ?? t('story.notFound')}</p>
      </div>
    );
  }

  if (type === 'conversation' && (errorMessage || !conversationResource || !conversationSituation)) {
    return (
      <div className={storyStyles.story}>
        <p className={storyStyles.story__error}>{t('story.resourceNotFound')}</p>
        <p className={storyStyles.story__errorHint}>{t('story.resourceCloseHint')}</p>
      </div>
    );
  }

  // Props del intro común (compartido entre intro y consuming).
  const introProps =
    type === 'story' && story
      ? {
          eyebrow: labelFor(story.category),
          title: localizedField(story, 'title', i18n.language),
          description: localizedField(story, 'description', i18n.language),
        }
      : conversationSituation
        ? (() => {
            // Las traducciones ES viven dentro del documento (snake_case
            // `_translation`, igual que los turnos). Misma señal que el resto de
            // la app: `i18n.language.startsWith('es')`, con fallback a EN.
            const es = i18n.language.startsWith('es');
            const sit = conversationSituation;
            const title =
              es && sit.title_translation?.trim() ? sit.title_translation : sit.title;
            const context =
              es && sit.context_translation?.trim() ? sit.context_translation : sit.context;
            const objective =
              es && sit.objective_translation?.trim()
                ? sit.objective_translation
                : sit.objective;
            return {
              // Mismo patrón que historias: el eyebrow muestra la categoría
              // (resuelta a nombre localizado), no el nivel.
              eyebrow: labelFor(conversationResource?.category),
              title,
              description: context,
              objective: objective?.trim()
                ? `${t('conversation.objective')}: ${objective.trim()}`
                : undefined,
            };
          })()
        : null;

  // Contenido de la fase de actividades (depende del tipo).
  let activitiesContent: React.ReactNode = null;
  if (phase === 'activities') {
    if (type === 'story' && story) {
      activitiesContent = (
        <ActivitiesTab
          key={location.key}
          activities={story.activities}
          onSubmit={(answers) => void handleSubmitStory(answers)}
          isSubmitting={isSubmitting}
          sourceType="story"
          sourceId={story.id}
          mistakeReviewSession={mistakeReviewSession}
          onRequestMistakeReview={navigateToMistakeReview}
        />
      );
    } else if (
      type === 'conversation' &&
      conversationResource &&
      conversationResource.activities?.length
    ) {
      activitiesContent = (
        <ActivitiesTab
          key={location.key}
          activities={conversationResource.activities}
          onSubmit={(answers) => void handleSubmitConversation(answers)}
          isSubmitting={isSubmitting}
          sourceType="conversation"
          sourceId={conversationResource.id}
          mistakeReviewSession={mistakeReviewSession}
          onRequestMistakeReview={navigateToMistakeReview}
        />
      );
    }
  }

  if (phase !== 'activities' && !introProps) return null;

  /**
   * Render directo sin `AnimatePresence`/variants. Cada sección anima su
   * propia opacidad ligada a `isLeaving`:
   *   - Hero (en el portal del layout): 350 ms (ver useEffect arriba).
   *   - Info de la historia: 350 ms.
   *   - Karaoke (`StoryStartedFlow`): 550 ms.
   *
   * Cuando `isLeaving` se activa, los tres bloques empiezan a fundirse a la
   * vez con sus duraciones distintas — la info se va antes que el karaoke.
   * Tras 600 ms `requestActivities` cambia `phase` a `activities`, momento
   * en el que todo el contenido de consuming ya está invisible y la fase
   * de actividades se monta con su propia entrada (fade + slide-up).
   */
  if (phase === 'activities' && activitiesContent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: [0.4, 0, 0.2, 1] }}
        className={cn(
          storyStyles.story,
          exitToResult && storyStyles['story--exitToResult']
        )}
      >
        {activitiesContent}
      </motion.div>
    );
  }

  if (!introProps) return null;

  return (
    <div className={storyStyles.story}>
      <motion.div
        initial={false}
        animate={{ opacity: isLeaving ? 0 : 1 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      >
        <ContentDetailIntro {...introProps} />
      </motion.div>
      {phase === 'consuming' && type === 'story' && story && (
        <motion.div
          initial={false}
          animate={{ opacity: isLeaving ? 0 : 1 }}
          transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
        >
          <StoryStartedFlow story={story} audioAlreadyListened={audioListened} />
        </motion.div>
      )}
      {phase === 'consuming' && type === 'conversation' && conversationDocument && (
        <motion.div
          initial={false}
          animate={{ opacity: isLeaving ? 0 : 1 }}
          transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
        >
          <ConversationStartedFlow
            document={conversationDocument}
            hasFollowUpActivities={!!conversationResource?.activities?.length}
            onContinueToActivities={requestActivities}
            onFinishWithoutActivities={(accuracy) => handleSubmitConversation([], accuracy)}
            onStudySecondsChange={onConversationStudySeconds}
          />
        </motion.div>
      )}
    </div>
  );
}

export default StoryDetailPage;
